/**
 * AOV par pays (ou par site) — un bâton horizontal par entrée du top 10, et le
 * panier moyen de toute la boutique en gris pour repère.
 *
 * Horizontal : dix noms se lisent mieux en ligne que penchés sous un axe.
 * Couleur par rang, la même que sur les courbes et les parts.
 * AOV = CA encaissé avant remboursements ÷ commandes, comme la tuile.
 */

import { Bar, BarChart, CartesianGrid, Cell, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { formatInteger, formatMoney } from '../lib/format';
import { AXIS_STYLE, BAR_RADIUS, COLORS, OTHER_COLOR } from '../lib/theme';
import type { DataColumn } from '../types';
import { ChartCard } from './Card';

export interface CountryAovBar {
  key: string;
  label: string;
  orders: number;
  aov: number;
  color: string;
}

function AovTooltip({ active, payload, currency }: { active?: boolean; payload?: Array<{ payload?: CountryAovBar }>; currency: string }) {
  const bar = payload?.[0]?.payload;
  if (!active || !bar) return null;
  return (
    <div className="tooltip" role="tooltip">
      <div className="tooltip__row">
        <span className="legend__key legend__key--area" style={{ background: bar.color }} aria-hidden="true" />
        <span className="tooltip__name">{bar.label}</span>
        <span className="tooltip__value">{formatMoney(bar.aov, currency, true)}</span>
      </div>
      <p className="tooltip__title">
        {formatInteger(bar.orders)} commande{bar.orders > 1 ? 's' : ''}
      </p>
    </div>
  );
}

export function CountryAovChart({
  bars,
  global,
  currency,
  noun = 'pays',
  labelWidth = 110,
}: {
  /** Largeur réservée aux libellés (les titres de produits sont longs). */
  labelWidth?: number;
  bars: readonly CountryAovBar[];
  /** « pays », « site » — pour le sous-titre. */
  noun?: string;
  /** Panier moyen de toute la boutique, en repère. */
  global: { aov: number; orders: number } | null;
  currency: string;
}) {
  const rows: CountryAovBar[] = [...bars];
  if (global) rows.push({ key: 'global', label: 'Global', orders: global.orders, aov: global.aov, color: OTHER_COLOR });
  const money = (value: number) => formatMoney(value, currency, true);

  const columns: ReadonlyArray<DataColumn<CountryAovBar>> = [
    { key: 'label', header: 'Pays', render: (row) => row.label },
    { key: 'orders', header: 'Commandes', numeric: true, render: (row) => formatInteger(row.orders) },
    { key: 'aov', header: 'AOV TTC', numeric: true, render: (row) => money(row.aov) },
  ];
  const axisTick = { fill: AXIS_STYLE.fill, fontSize: AXIS_STYLE.fontSize };

  return (
    <ChartCard
      title="AOV (TTC)"
      subtitle={`Panier moyen TTC par ${noun}, avant remboursements — la boutique entière en gris`}
      rows={rows}
      columns={columns}
      emptyMessage="Aucune commande sur cette période."
      chartHeight={Math.max(240, rows.length * 30 + 30)}
    >
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={rows} layout="vertical" margin={{ top: 4, right: 72, bottom: 0, left: 0 }}>
          <CartesianGrid stroke={COLORS.grid} horizontal={false} />
          <XAxis type="number" tickFormatter={(value: number) => formatMoney(value, currency)} tick={axisTick} axisLine={false} tickLine={false} />
          <YAxis type="category" dataKey="label" tick={axisTick} axisLine={{ stroke: COLORS.grid }} tickLine={false} width={labelWidth} />
          <Tooltip cursor={{ fill: 'rgb(255 255 255 / 0.04)' }} content={<AovTooltip currency={currency} />} />
          <Bar dataKey="aov" name="AOV" radius={[0, BAR_RADIUS, BAR_RADIUS, 0]} maxBarSize={20} isAnimationActive={false}>
            {rows.map((row) => (
              <Cell key={row.key} fill={row.color} />
            ))}
            <LabelList
              dataKey="aov"
              position="right"
              formatter={(value: unknown) => (typeof value === 'number' ? money(value) : '')}
              fill={COLORS.textSecondary}
              fontSize={12}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}
